/**
 * AdminDashboard
 * Main admin interface for session management per event
 * Feature: User Management (002-user-management) - Phase 3, Phase 4 US3, Phase 5 US4
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import ApiService, { API_BASE_URL } from '../services/ApiService';
import AdminApiService from '../services/AdminApiService';
import EventSelector from './EventSelector';
import SessionTableEditor from './SessionTableEditor';
import ConflictNotification from './ConflictNotification';
import PreviewPanel from './PreviewPanel'; // T088 Phase 4
import PictureUploadZone from './PictureUploadZone'; // T110 Phase 5
import FeedbackViewer from './FeedbackViewer'; // Feedback viewer
import useConflictDetection from '../hooks/useConflictDetection';
import UserMenu from './UserMenu';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('sessions'); // T110 Phase 5: sessions, pictures, feedback
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [segmentDisabled, setSegmentDisabled] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all, draft, published
  const [showPreview, setShowPreview] = useState(false); // T089 Phase 4
  const [publishErrors, setPublishErrors] = useState(null); // Store publish errors
  const [assignmentResults, setAssignmentResults] = useState(null); // Store picture assignment results
  const [presenters, setPresenters] = useState([]); // Presenters Info tab
  const [presentersLoading, setPresentersLoading] = useState(false);
  const [presenterSearch, setPresenterSearch] = useState('');
  const [presenterFilters, setPresenterFilters] = useState({});
  const [editingPresenterRow, setEditingPresenterRow] = useState(null);
  const [editingPresenterData, setEditingPresenterData] = useState({});
  const [presenterExtras, setPresenterExtras] = useState({}); // { [presenterKey]: { segment, emails } }

  const ADMIN_SEGMENT_LIST = ['OPS-SW', 'Grow@PLM', 'LCS', 'ME', 'PES', 'SIM'];
  const [selectedSegment, setSelectedSegment] = useState(() => user?.segment || 'OPS-SW');
  const effectiveSegment = user?.role === 'super_admin' ? selectedSegment : (user?.segment || 'OPS-SW');
  
  const {
    conflicts,
    showConflictNotification,
    addConflict,
    dismissNotification
  } = useConflictDetection();

  const SEGMENT_OPTIONS = ['OPS-SW', 'LCS', 'ME', 'PES', 'SIM', 'Grow@PLM', 'Other'];

  // Load presenter extras from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('presenterExtras');
      if (saved) setPresenterExtras(JSON.parse(saved));
    } catch (_) { /* ignore */ }
  }, []);

  // Persist presenter extras to localStorage whenever they change
  const updatePresenterExtras = (key, data) => {
    setPresenterExtras(prev => {
      const next = { ...prev, [key]: { ...(prev[key] || {}), ...data } };
      localStorage.setItem('presenterExtras', JSON.stringify(next));
      return next;
    });
  };

  const getPresenterKey = (p) => (p.presenter_name || p.presenterName || '').toLowerCase().trim();

  useEffect(() => {
    if (selectedEventId) {
      loadSessions();
    } else {
      setSessions([]);
    }
  }, [selectedEventId, statusFilter, effectiveSegment]);

  // Check if admin's segment is enabled for the selected event
  useEffect(() => {
    const checkSegment = async () => {
      if (!selectedEventId || !effectiveSegment) {
        setSegmentDisabled(false);
        return;
      }
      try {
        const response = await ApiService.get('/sessions/events');
        const events = response.data || [];
        const evt = events.find(e => String(e.id) === String(selectedEventId));
        if (evt && evt.enabledSegments) {
          // Normalize admin segment to match DB format (e.g. 'Grow@PLM' → 'GROW_PLM')
          const segMap = { 'Grow@PLM': 'GROW_PLM', 'OPS-SW': 'OPS_SW', 'LCS': 'LCS', 'ME': 'ME', 'PES': 'PES', 'SIM': 'SIM' };
          const normalized = segMap[effectiveSegment] || effectiveSegment.toUpperCase().replace(/[@\s]/g, '_');
          setSegmentDisabled(!evt.enabledSegments.includes(normalized));
        } else {
          setSegmentDisabled(false);
        }
      } catch (err) {
        console.error('Failed to check segment status:', err);
        setSegmentDisabled(false);
      }
    };
    checkSegment();
  }, [selectedEventId, effectiveSegment]);

  // Listen for session refresh events (e.g., after picture upload)
  useEffect(() => {
    const handleRefresh = () => {
      if (selectedEventId) {
        loadSessions();
      }
    };
    window.addEventListener('refreshSessions', handleRefresh);
    return () => window.removeEventListener('refreshSessions', handleRefresh);
  }, [selectedEventId]);

  const loadSessions = async () => {
    console.log('AdminDashboard loadSessions - selectedEventId:', selectedEventId, 'effectiveSegment:', effectiveSegment);
    if (!selectedEventId) return;

    setLoading(true);
    setError(null);

    try {
      const filter = statusFilter === 'all' ? null : statusFilter;
      console.log('AdminDashboard loadSessions - calling API with eventId:', selectedEventId);
      const response = await AdminApiService.getSessions(selectedEventId, filter, effectiveSegment);
      
      // DEBUG: Log the full response to check for presenterPictureUrl
      console.log('[DEBUG] AdminDashboard - API response:', {
        totalSessions: response.data?.length || 0,
        firstThreeSessions: response.data?.slice(0, 3),
        sessionsWithPictures: response.data?.filter(s => s.presenterPictureUrl).map(s => ({
          id: s.id,
          name: s.name,
          presenter: s.presenter,
          presenterPictureUrl: s.presenterPictureUrl
        }))
      });
      
      setSessions(response.data || []);
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = (eventId) => {
    console.log('AdminDashboard handleEventChange - eventId:', eventId);
    console.log('AdminDashboard handleEventChange - type:', typeof eventId);
    setSelectedEventId(eventId);
    setUploadStatus(null);
    setError(null);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!selectedEventId) {
      alert('Please select an event first');
      return;
    }

    setLoading(true);
    setUploadStatus(null);
    setError(null);

    try {
      const response = await AdminApiService.uploadExcelForEvent(
        file,
        effectiveSegment,
        selectedEventId
      );

      setUploadStatus({
        success: true,
        total: response.total,
        successful: response.successful,
        failed: response.failed,
        errors: response.errors
      });

      // Reload sessions after upload
      await loadSessions();
    } catch (err) {
      console.error('Excel upload failed:', err);
      setError(err.message || 'Failed to upload Excel file');
      setUploadStatus({
        success: false,
        error: err.message
      });
    } finally {
      setLoading(false);
      // Reset file input
      event.target.value = null;
    }
  };

  const handleUpdateSession = async (sessionId, updates, clientUpdatedAt) => {
    setError(null);

    try {
      const response = await AdminApiService.updateSession(sessionId, updates, clientUpdatedAt);
      
      // Check for conflict warning
      if (response.warning) {
        const session = sessions.find(s => s.id === sessionId);
        addConflict(response.warning, sessionId, session?.name || 'Unknown');
      }

      // Update local state
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? response.data : s
      ));
    } catch (err) {
      console.error('Failed to update session:', err);
      setError(err.message || 'Failed to update session');
      throw err; // Re-throw so editor can handle it
    }
  };

  const handleDeleteSession = async (sessionId) => {
    setError(null);

    try {
      await AdminApiService.deleteSession(sessionId);
      
      // Remove from local state
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError(err.message || 'Failed to delete session');
      throw err;
    }
  };

  const handlePublishSession = async (sessionId) => {
    setError(null);

    try {
      const response = await AdminApiService.publishSession(sessionId);
      
      // Update local state
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? response.data : s
      ));
    } catch (err) {
      console.error('Failed to publish session:', err);
      setError(err.message || 'Failed to publish session');
      throw err;
    }
  };

  const handleUnpublishSession = async (sessionId) => {
    setError(null);

    try {
      const response = await AdminApiService.unpublishSession(sessionId);
      
      // Update local state
      setSessions(prev => prev.map(s => 
        s.id === sessionId ? response.data : s
      ));
    } catch (err) {
      console.error('Failed to unpublish session:', err);
      setError(err.message || 'Failed to unpublish session');
      throw err;
    }
  };

  const handleAddSession = async (sessionData) => {
    setError(null);

    try {
      // Add event ID and segment to session data
      sessionData.eventId = selectedEventId;
      sessionData.segment = effectiveSegment;
      const wantPublish = sessionData.status === 'published';

      const response = await AdminApiService.createSession(sessionData);
      const created = response.data;

      if (wantPublish) {
        await AdminApiService.publishSession(created.id);
        created.status = 'published';
      }

      // Add to local state
      setSessions(prev => [...prev, created]);
    } catch (err) {
      console.error('Failed to add session:', err);
      
      // Format error message with validation details if available
      let errorMessage = 'Failed to add session';
      if (err.details && Array.isArray(err.details)) {
        errorMessage += ':\n\n' + err.details.map(d => 
          `• ${d.field}: ${d.message}`
        ).join('\n');
      } else {
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
      throw err;
    }
  };

  /**
   * Handle preview button click
   * Phase 4 US3 - T089
   */
  const handlePreview = () => {
    if (!selectedEventId) {
      alert('Please select an event first');
      return;
    }
    setShowPreview(true);
  };

  /**
   * Handle bulk publish button click
   * Phase 4 US3 - T090
   */
  const handleBulkPublish = async () => {
    if (!selectedEventId) {
      alert('Please select an event first');
      return;
    }

    // Count draft sessions
    const draftSessions = sessions.filter(s => s.status === 'draft');
    if (draftSessions.length === 0) {
      alert('No draft sessions to publish for this event');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to publish all ${draftSessions.length} draft session(s)?\n\n` +
      `This will make them visible on the public dashboard.`
    );

    if (!confirmed) return;

    setLoading(true);
    setError(null);

    try {
      const response = await AdminApiService.bulkPublishSessions(selectedEventId, null, effectiveSegment);
      
      // Store errors if any
      if (response.data.errors && response.data.errors.length > 0) {
        setPublishErrors({
          published: response.data.published,
          failed: response.data.failed,
          errors: response.data.errors
        });
      } else {
        alert(`✓ ${response.data.message}`);
      }

      // Reload sessions to reflect updated status
      await loadSessions();
    } catch (err) {
      console.error('Bulk publish failed:', err);
      setError(err.message || 'Failed to publish sessions');
      alert('Failed to publish sessions: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDraftAll = async () => {
    if (!selectedEventId) {
      alert('Please select an event first');
      return;
    }
    const nonDraft = sessions.filter(s => s.status !== 'draft');
    const total = sessions.length;
    if (total === 0) {
      alert('No sessions found for this event');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to set all ${total} session(s) to Draft?\n\n` +
      `This will unpublish any currently published sessions.`
    );
    if (!confirmed) return;
    setLoading(true);
    setError(null);
    try {
      const response = await AdminApiService.draftAllSessions(selectedEventId, effectiveSegment);
      alert(`✓ ${response.data.message}`);
      await loadSessions();
    } catch (err) {
      console.error('Draft all failed:', err);
      setError(err.message || 'Failed to draft all sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!selectedEventId) {
      alert('Please select an event first');
      return;
    }
    if (sessions.length === 0) {
      alert('No sessions found for this event');
      return;
    }
    const confirmed = window.confirm(
      `⚠ Are you sure you want to DELETE ALL ${sessions.length} session(s)?\n\n` +
      `This action cannot be undone.`
    );
    if (!confirmed) return;
    // Second confirmation for safety
    const confirmed2 = window.confirm(
      `Please confirm again: permanently delete all ${sessions.length} session(s) for segment ${effectiveSegment}?`
    );
    if (!confirmed2) return;
    setLoading(true);
    setError(null);
    try {
      const response = await AdminApiService.deleteAllSessions(selectedEventId, effectiveSegment);
      alert(`✓ ${response.data.message}`);
      await loadSessions();
    } catch (err) {
      console.error('Delete all failed:', err);
      setError(err.message || 'Failed to delete all sessions');
    } finally {
      setLoading(false);
    }
  };

  const copyErrorsToClipboard = () => {
    if (!publishErrors) return;
    
    let text = `Publish Results:\n`;
    text += `Published: ${publishErrors.published}\n`;
    text += `Failed: ${publishErrors.failed}\n\n`;
    text += `Errors:\n`;
    publishErrors.errors.forEach((err, idx) => {
      text += `${idx + 1}. ${err.sessionName || err.sessionId}\n`;
      text += `   Error: ${err.error}\n`;
      if (err.details) {
        text += `   Details: ${JSON.stringify(err.details, null, 2)}\n`;
      }
      text += `\n`;
    });
    
    navigator.clipboard.writeText(text).then(() => {
      alert('Errors copied to clipboard!');
    });
  };

  /**
   * Handle picture upload complete
   * Phase 5 US4 - T110
   */
  const handlePictureUploadComplete = async (result) => {
    console.log('Pictures uploaded:', result);
    
    // Auto-assign only newly uploaded pictures that match presenter names
    if (selectedEventId && result.results && result.results.length > 0) {
      try {
        // Only include newly uploaded pictures that match a presenter name
        const pictureIds = result.results
          ?.filter(r => r.status === 'uploaded' && r.matchesPresenter && r.pictureId)
          .map(r => r.pictureId) || [];
        
        if (pictureIds.length > 0) {
          const token = localStorage.getItem('authToken');
          const assignResponse = await fetch(`${API_BASE_URL}/admin/pictures/assign`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              eventId: selectedEventId,
              pictureIds
            })
          });
          
          if (assignResponse.ok) {
            const assignResult = await assignResponse.json();
            console.log('Pictures assigned:', assignResult);
            
            // Store assignment results to show user
            setAssignmentResults(assignResult);
            
            // Auto-dismiss after 10 seconds if all matched
            if (assignResult.unmatched === 0) {
              setTimeout(() => setAssignmentResults(null), 10000);
            }
          } else {
            console.warn('Picture assignment failed:', await assignResponse.text());
            setError('Failed to assign pictures to sessions');
          }
        }
      } catch (error) {
        console.error('Failed to assign pictures:', error);
        setError('Failed to assign pictures: ' + error.message);
      }
    }
    
    // Reload sessions to show updated pictures
    if (selectedEventId) {
      loadSessions();
    }
  };

  /**
   * Handle tab change
   * Phase 5 US4 - T110
   */
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setError(null);
    if (tab === 'presenters-info') {
      loadPresenters();
    }
  };

  const loadPresenters = async () => {
    setPresentersLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/admin/pictures`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        const all = result.data || [];
        // Deduplicate: keep only the latest picture per presenter name, then sort alphabetically
        const latestByName = {};
        for (const p of all) {
          const name = (p.presenter_name || p.presenterName || '').toLowerCase().trim();
          if (!name) continue;
          const uploadedAt = p.uploaded_at || p.uploadedAt || '';
          if (!latestByName[name] || uploadedAt > (latestByName[name].uploaded_at || latestByName[name].uploadedAt || '')) {
            latestByName[name] = p;
          }
        }
        const deduplicated = Object.values(latestByName).sort((a, b) => {
          const nameA = (a.presenter_name || a.presenterName || '').toLowerCase();
          const nameB = (b.presenter_name || b.presenterName || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setPresenters(deduplicated);
      } else {
        setError('Failed to load presenters');
      }
    } catch (err) {
      console.error('Failed to load presenters:', err);
      setError('Failed to load presenters: ' + err.message);
    } finally {
      setPresentersLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">
      {showConflictNotification && (
        <ConflictNotification 
          conflicts={conflicts}
          onDismiss={dismissNotification}
        />
      )}

      {/* Phase 4: Preview Panel Modal */}
      {showPreview && (
        <PreviewPanel 
          eventId={selectedEventId}
          onClose={() => setShowPreview(false)}
        />
      )}

      <header className="admin-header">
        <div className="header-content">
          <div className="header-left">
            <h1>Admin Dashboard</h1>
            {user?.role === 'super_admin' ? (
              <div className="superadmin-segment-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
                <span className="user-badge" style={{ background: '#EDE9FE', color: '#6D28D9' }}>Super Admin</span>
                <label htmlFor="admin-segment-select" style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>Segment:</label>
                <select
                  id="admin-segment-select"
                  value={selectedSegment}
                  onChange={(e) => setSelectedSegment(e.target.value)}
                  style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #fff', fontWeight: 600, fontSize: '13px', background: '#fff', color: '#00646E', cursor: 'pointer' }}
                >
                  {ADMIN_SEGMENT_LIST.map(seg => (
                    <option key={seg} value={seg}>{seg}</option>
                  ))}
                </select>
              </div>
            ) : (
              <span className="user-badge">
                {user.name || user.username} ({user.segment})
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={logout} className="btn-logout">
              Logout
            </button>
            <UserMenu />
          </div>
        </div>
        
        {/* Phase 5 US4 - T110: Tab Navigation */}
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'sessions' ? 'active' : ''}`}
            onClick={() => handleTabChange('sessions')}
          >
            📅 Session Management
          </button>
          <button 
            className={`admin-tab ${activeTab === 'pictures' ? 'active' : ''}`}
            onClick={() => handleTabChange('pictures')}
          >
            🖼️ Presenter Pictures
          </button>
          <button 
            className={`admin-tab ${activeTab === 'presenters-info' ? 'active' : ''}`}
            onClick={() => handleTabChange('presenters-info')}
          >
            👤 Presenters Info
          </button>
          <button 
            className={`admin-tab ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => handleTabChange('feedback')}
          >
            📝 User Feedback
          </button>
        </div>
      </header>

      <div className="admin-content">
        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <>
            <div className="control-panel">
              <div className="control-row">
                <div className="control-group">
                  <label>Select Event:</label>
                  <EventSelector 
                    value={selectedEventId}
                    onChange={handleEventChange}
                    filter="all"
                  />
                </div>

                <div className="control-group">
                  <label>Status Filter:</label>
                  <select 
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="status-filter"
                    disabled={!selectedEventId}
                  >
                    <option value="all">All Sessions</option>
                    <option value="draft">Draft Only</option>
                    <option value="published">Published Only</option>
                  </select>
                </div>

                <div className="control-group">
                  <label>Import Excel:</label>
                  <label className={`btn-upload${segmentDisabled ? ' disabled' : ''}`} htmlFor={segmentDisabled ? undefined : 'excel-upload'}>
                    📁 Choose File
                  </label>
                  <input
                    id="excel-upload"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    disabled={!selectedEventId || loading || segmentDisabled}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>

              {/* Phase 4: Action Buttons Row */}
              <div className="control-row action-row">
                <button 
                  className="btn-action btn-preview"
                  onClick={handlePreview}
                  disabled={!selectedEventId || loading || segmentDisabled}
                  title="Preview how sessions will appear on public dashboard"
                >
                  📱 Preview Dashboard
                </button>

                <button 
                  className="btn-action btn-bulk-publish"
                  onClick={handleBulkPublish}
                  disabled={!selectedEventId || loading || segmentDisabled}
                  title="Publish all draft sessions for this event"
                >
                  🚀 Publish All Drafts
                </button>

                <button
                  className="btn-action btn-draft-all"
                  onClick={handleDraftAll}
                  disabled={!selectedEventId || loading || segmentDisabled}
                  title="Set all sessions to Draft (unpublishes published sessions)"
                >
                  📝 Draft All Sessions
                </button>

                <button
                  className="btn-action btn-delete-all"
                  onClick={handleDeleteAll}
                  disabled={!selectedEventId || loading || segmentDisabled}
                  title="Permanently delete all sessions for this event"
                >
                  🗑️ Delete All Sessions
                </button>
              </div>

              {uploadStatus && (
                <div className={`upload-status ${uploadStatus.success ? 'success' : 'error'}`} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setUploadStatus(null)}
                    style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, opacity: 0.6 }}
                    title="Dismiss"
                    aria-label="Dismiss"
                  >✕</button>
                  {uploadStatus.success ? (
                    <>
                      <p>✓ Excel imported successfully</p>
                      <p>
                        Total: {uploadStatus.total} | 
                        Successful: {uploadStatus.successful} | 
                        Failed: {uploadStatus.failed}
                      </p>
                      {uploadStatus.errors.length > 0 && (
                        <details>
                          <summary>View Errors ({uploadStatus.errors.length})</summary>
                          <ul>
                            {uploadStatus.errors.map((err, idx) => {
                              const errorMessages = Array.isArray(err.errors)
                                ? err.errors.map(e => typeof e === 'string' ? e : (e.message || JSON.stringify(e)))
                                : [err.errors?.message || err.errors || 'Unknown error'];
                              return (
                                <li key={idx}>
                                  Row {err.row}: {errorMessages.join(', ')}
                                </li>
                              );
                            })}
                          </ul>
                        </details>
                      )}
                    </>
                  ) : (
                    <p>✗ {uploadStatus.error}</p>
                  )}
                </div>
              )}

              {segmentDisabled && selectedEventId && (
                <div className="segment-disabled-banner">
                  ⚠️ Segment ({effectiveSegment}) is not enabled for this event. Session upload and creation are disabled.
                </div>
              )}

              {error && (
                <div className="error-banner">
                  {error}
                </div>
              )}
            </div>

            {selectedEventId ? (
              <SessionTableEditor
                sessions={sessions}
                loading={loading}
                error={error}
                onUpdate={handleUpdateSession}
                onDelete={handleDeleteSession}
                onPublish={handlePublishSession}
                onUnpublish={handleUnpublishSession}
                onAdd={handleAddSession}
              />
            ) : (
              <div className="empty-state-card">
                <p>👆 Select an event to view and manage sessions</p>
              </div>
            )}
          </>
        )}

        {/* Pictures Tab - Phase 5 US4 - T110 */}
        {activeTab === 'pictures' && (
          <>
            <div className="pictures-tab-content">
              <div className="pictures-header">
                <h2>Presenter Picture Management</h2>
                <p>Upload presenter pictures to display on session cards. Filenames should match presenter names for automatic assignment.</p>
              </div>

              {error && (
                <div className="error-banner">
                  {error}
                </div>
              )}

              <PictureUploadZone onUploadComplete={handlePictureUploadComplete} eventId={selectedEventId} />
            </div>
          </>
        )}

        {/* Presenters Info Tab */}
        {activeTab === 'presenters-info' && (
          <div className="presenters-info-tab">
            <div className="presenters-info-header">
              <h2>Presenters Info</h2>
              <p>View all presenter pictures and names. Use filters, search, and inline editing to manage presenter data.</p>
              {selectedEventId && (
                <button
                  className="btn-reassign-all"
                  title="Re-run photo assignment for all presenters to fix missing photos on session cards"
                  onClick={async () => {
                    if (!selectedEventId) { alert('Please select an event first'); return; }
                    try {
                      const token = localStorage.getItem('authToken');
                      const resp = await fetch(`${API_BASE_URL}/admin/pictures/assign`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ eventId: selectedEventId })
                      });
                      if (resp.ok) {
                        const result = await resp.json();
                        alert(`✓ Re-assigned photos: ${result.matched} matched, ${result.unmatched} unmatched`);
                        loadSessions();
                      } else {
                        const err = await resp.json();
                        alert('Failed: ' + (err.error || resp.statusText));
                      }
                    } catch (err) {
                      alert('Failed to re-assign: ' + err.message);
                    }
                  }}
                >
                  🔗 Re-assign All Photos to Sessions
                </button>
              )}
            </div>
            {presentersLoading ? (
              <div className="loading-state">Loading presenters...</div>
            ) : presenters.length === 0 ? (
              <div className="empty-state-card">
                <p>No presenter pictures uploaded yet.</p>
              </div>
            ) : (() => {
              // Compute filtered presenters
              const filtered = presenters.filter(p => {
                const name = (p.presenter_name || p.presenterName || '').toLowerCase();
                const key = getPresenterKey(p);
                const extras = presenterExtras[key] || {};
                const segment = extras.segment || '';
                const emails = extras.emails || '';

                // Global search
                if (presenterSearch) {
                  const q = presenterSearch.toLowerCase();
                  if (
                    !name.includes(q) &&
                    !segment.toLowerCase().includes(q) &&
                    !emails.toLowerCase().includes(q)
                  ) return false;
                }

                // Column filters
                if (presenterFilters.name) {
                  if (!name.includes(presenterFilters.name.toLowerCase())) return false;
                }
                if (presenterFilters.segment) {
                  if (segment !== presenterFilters.segment) return false;
                }
                if (presenterFilters.email) {
                  if (!emails.toLowerCase().includes(presenterFilters.email.toLowerCase())) return false;
                }

                return true;
              });

              return (
                <>
                  {/* Search bar */}
                  <div className="presenters-search-bar">
                    <input
                      type="text"
                      placeholder="🔍 Search presenters by name, segment, or email..."
                      value={presenterSearch}
                      onChange={e => setPresenterSearch(e.target.value)}
                      className="presenters-search-input"
                    />
                    {presenterSearch && (
                      <button className="presenters-search-clear" onClick={() => setPresenterSearch('')}>✕</button>
                    )}
                  </div>

                  <div className="presenters-table-wrapper">
                    <table className="presenters-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>
                            Presenter Name
                            <div className="th-filter">
                              <input
                                type="text"
                                placeholder="Filter..."
                                value={presenterFilters.name || ''}
                                onChange={e => setPresenterFilters(f => ({ ...f, name: e.target.value }))}
                                className="column-filter-input"
                              />
                            </div>
                          </th>
                          <th>Presenter Image</th>
                          <th>
                            Segment
                            <div className="th-filter">
                              <select
                                value={presenterFilters.segment || ''}
                                onChange={e => setPresenterFilters(f => ({ ...f, segment: e.target.value }))}
                                className="column-filter-select"
                              >
                                <option value="">All</option>
                                {SEGMENT_OPTIONS.map(s => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                          </th>
                          <th>
                            Presenter Email
                            <div className="th-filter">
                              <input
                                type="text"
                                placeholder="Filter..."
                                value={presenterFilters.email || ''}
                                onChange={e => setPresenterFilters(f => ({ ...f, email: e.target.value }))}
                                className="column-filter-input"
                              />
                            </div>
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filtered.map((p, idx) => {
                          const key = getPresenterKey(p);
                          const extras = presenterExtras[key] || {};
                          const isEditing = editingPresenterRow === (p.id || idx);

                          return (
                            <tr key={p.id || idx}>
                              <td>{idx + 1}</td>
                              <td>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    value={editingPresenterData.name ?? (p.presenter_name || p.presenterName || '')}
                                    onChange={e => setEditingPresenterData(d => ({ ...d, name: e.target.value }))}
                                    className="inline-edit-input"
                                  />
                                ) : (
                                  p.presenter_name || p.presenterName || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <div className="presenter-image-edit">
                                    {(p.file_path || p.filePath) && !editingPresenterData._imageRemoved ? (
                                      <div className="presenter-image-preview">
                                        <img
                                          src={`/static/pictures/${p.stored_filename || p.storedFilename}?v=${p.uploaded_at || p.uploadedAt ? new Date(p.uploaded_at || p.uploadedAt).getTime() : p.id}`}
                                          alt={p.presenter_name || p.presenterName}
                                          className="presenter-thumb"
                                        />
                                        <button
                                          className="btn-remove-image"
                                          title="Remove image"
                                          onClick={() => setEditingPresenterData(d => ({ ...d, _imageRemoved: true, _newImageFile: null }))}
                                        >✕</button>
                                      </div>
                                    ) : (
                                      <label className="presenter-image-upload-label">
                                        <input
                                          type="file"
                                          accept="image/*"
                                          style={{ display: 'none' }}
                                          onChange={e => {
                                            const file = e.target.files[0];
                                            if (file) setEditingPresenterData(d => ({ ...d, _newImageFile: file, _imageRemoved: true }));
                                          }}
                                        />
                                        {editingPresenterData._newImageFile ? (
                                          <span className="new-image-name">{editingPresenterData._newImageFile.name}</span>
                                        ) : (
                                          <span className="upload-placeholder">📷 Upload image</span>
                                        )}
                                      </label>
                                    )}
                                  </div>
                                ) : (
                                  p.file_path || p.filePath ? (
                                    <img
                                      src={`/static/pictures/${p.stored_filename || p.storedFilename}?v=${p.uploaded_at || p.uploadedAt ? new Date(p.uploaded_at || p.uploadedAt).getTime() : p.id}`}
                                      alt={p.presenter_name || p.presenterName}
                                      className="presenter-thumb"
                                    />
                                  ) : (
                                    <span className="no-image">No image</span>
                                  )
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <>
                                    <select
                                      value={editingPresenterData.segment ?? (extras.segment || '')}
                                      onChange={e => setEditingPresenterData(d => ({ ...d, segment: e.target.value }))}
                                      className="inline-edit-select"
                                    >
                                      <option value="">— Select —</option>
                                      {SEGMENT_OPTIONS.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                      ))}
                                    </select>
                                    {(editingPresenterData.segment ?? extras.segment) === 'Other' && (
                                      <input
                                        type="text"
                                        placeholder="Specify..."
                                        value={editingPresenterData.segmentOther ?? (extras.segmentOther || '')}
                                        onChange={e => setEditingPresenterData(d => ({ ...d, segmentOther: e.target.value }))}
                                        className="inline-edit-input segment-other-input"
                                      />
                                    )}
                                  </>
                                ) : (
                                  extras.segment
                                    ? (extras.segment === 'Other' && extras.segmentOther ? `Other: ${extras.segmentOther}` : extras.segment)
                                    : <span className="not-set">—</span>
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    placeholder="email1@example.com, email2@example.com"
                                    value={editingPresenterData.emails ?? (extras.emails || '')}
                                    onChange={e => setEditingPresenterData(d => ({ ...d, emails: e.target.value }))}
                                    className="inline-edit-input email-input"
                                  />
                                ) : (
                                  extras.emails
                                    ? <span className="email-list">{extras.emails}</span>
                                    : <span className="not-set">—</span>
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <div className="edit-actions">
                                    <button
                                      className="btn-save-row"
                                      title="Save"
                                      onClick={async () => {
                                        const token = localStorage.getItem('authToken');
                                        // Handle image removal
                                        if (editingPresenterData._imageRemoved && !editingPresenterData._newImageFile && p.id) {
                                          try {
                                            await fetch(`${API_BASE_URL}/admin/pictures/${p.id}`, {
                                              method: 'DELETE',
                                              headers: { 'Authorization': `Bearer ${token}` }
                                            });
                                          } catch (err) {
                                            console.error('Failed to delete image:', err);
                                          }
                                        }
                                        // Handle new image upload
                                        if (editingPresenterData._newImageFile) {
                                          try {
                                            // Delete old image first if it existed
                                            if (p.id && (p.file_path || p.filePath)) {
                                              await fetch(`${API_BASE_URL}/admin/pictures/${p.id}`, {
                                                method: 'DELETE',
                                                headers: { 'Authorization': `Bearer ${token}` }
                                              });
                                            }
                                            const formData = new FormData();
                                            formData.append('pictures', editingPresenterData._newImageFile);
                                            const uploadResp = await fetch(`${API_BASE_URL}/admin/pictures/upload${selectedEventId ? `?eventId=${selectedEventId}` : ''}`, {
                                              method: 'POST',
                                              headers: { 'Authorization': `Bearer ${token}` },
                                              body: formData
                                            });
                                            // Auto-assign uploaded picture to matching sessions
                                            if (uploadResp.ok && selectedEventId) {
                                              const uploadResult = await uploadResp.json();
                                              const pictureIds = uploadResult.results
                                                ?.filter(r => r.status === 'uploaded' && r.pictureId)
                                                .map(r => r.pictureId) || [];
                                              if (pictureIds.length > 0) {
                                                await fetch(`${API_BASE_URL}/admin/pictures/assign`, {
                                                  method: 'POST',
                                                  headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                                                  body: JSON.stringify({ eventId: selectedEventId, pictureIds })
                                                });
                                              }
                                            }
                                          } catch (err) {
                                            console.error('Failed to upload image:', err);
                                          }
                                        }
                                        updatePresenterExtras(key, {
                                          segment: editingPresenterData.segment ?? extras.segment ?? '',
                                          segmentOther: editingPresenterData.segmentOther ?? extras.segmentOther ?? '',
                                          emails: editingPresenterData.emails ?? extras.emails ?? ''
                                        });
                                        setEditingPresenterRow(null);
                                        setEditingPresenterData({});
                                        loadPresenters();
                                      }}
                                    >✓</button>
                                    <button
                                      className="btn-cancel-row"
                                      title="Cancel"
                                      onClick={() => {
                                        setEditingPresenterRow(null);
                                        setEditingPresenterData({});
                                      }}
                                    >✕</button>
                                  </div>
                                ) : (
                                  <div className="edit-actions">
                                    <button
                                      className="btn-edit-row"
                                      title="Edit"
                                      onClick={() => {
                                        setEditingPresenterRow(p.id || idx);
                                        setEditingPresenterData({
                                          name: p.presenter_name || p.presenterName || '',
                                          segment: extras.segment || '',
                                          segmentOther: extras.segmentOther || '',
                                          emails: extras.emails || ''
                                        });
                                      }}
                                    >✏️ Edit</button>
                                    <button
                                      className="btn-remove-row"
                                      title="Remove presenter"
                                      onClick={async () => {
                                        if (!window.confirm(`Remove presenter "${p.presenter_name || p.presenterName}"? This will delete the image and all data for this presenter.`)) return;
                                        try {
                                          const token = localStorage.getItem('authToken');
                                          if (p.id) {
                                            await fetch(`${API_BASE_URL}/admin/pictures/${p.id}`, {
                                              method: 'DELETE',
                                              headers: { 'Authorization': `Bearer ${token}` }
                                            });
                                          }
                                          // Remove presenter extras from local storage
                                          setPresenterExtras(prev => {
                                            const next = { ...prev };
                                            delete next[key];
                                            localStorage.setItem('presenterExtras', JSON.stringify(next));
                                            return next;
                                          });
                                          loadPresenters();
                                        } catch (err) {
                                          console.error('Failed to remove presenter:', err);
                                          setError('Failed to remove presenter: ' + err.message);
                                        }
                                      }}
                                    >🗑️ Remove</button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        {filtered.length === 0 && (
                          <tr>
                            <td colSpan="6" className="no-results-cell">No presenters match your search/filter criteria.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <FeedbackViewer />
        )}
      </div>

      {/* Publish Errors Modal */}
      {publishErrors && (
        <div className="modal-overlay" onClick={() => setPublishErrors(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Publish Results</h3>
              <button className="modal-close" onClick={() => setPublishErrors(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="publish-summary">
                <p>✓ Published: <strong>{publishErrors.published}</strong></p>
                <p>✗ Failed: <strong>{publishErrors.failed}</strong></p>
              </div>
              <div className="error-list">
                <h4>Errors:</h4>
                {publishErrors.errors.map((err, idx) => (
                  <div key={idx} className="error-item">
                    <strong>{idx + 1}. {err.sessionName || err.sessionId}</strong>
                    <p>Error: {err.error}</p>
                    {err.details && (
                      <pre>{JSON.stringify(err.details, null, 2)}</pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-copy" onClick={copyErrorsToClipboard}>
                📋 Copy Errors to Clipboard
              </button>
              <button className="btn-close" onClick={() => setPublishErrors(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
